/**
 * PaymentMethodPicker — selects the active payment method.
 *
 * DES-78 / Stage 08.4+08.5 WAVE-4 REDESIGN + P0 HOTFIX 2026-05-22.
 * Card-grid layout per Figma 319:4631 / 319:4831 / 319:4931 / 40000032:6614 / 319:4731.
 *
 * P0 HOTFIX (2026-05-22):
 *   - Inactive card → bg-glass-16 (раньше было bg-transparent, карты «летали»)
 *   - Label / subtitle weight → font-semibold (600), раньше Bold/Medium — дрифт
 *   - «Не работает в РФ» override УДАЛЁН — в Figma 319:4631 subtitle для card
 *     ВСЕГДА «MasterCard, Visa», локального override-варианта нет
 *
 * Figma spec (live snapshot 319:4631 / 319:4831 / 319:4931 / 40000032:6614 / 319:4731):
 *   - Each card 86.5×98, cornerRadius 16
 *   - Icon 32×32 top
 *   - Label Raleway SemiBold 12 #fff line-height 14.088
 *   - Subtitle Raleway SemiBold 10 #fff line-height 11.74
 *   - All cards (active + inactive): fill #ffffff @16% (bg-glass-16)
 *   - Active: + 1px orange gradient stroke #FFC24C → #FF9012 (~135°)
 *   - Inactive: no stroke
 *   - Row: gap 8 (gap-2), 4 cards or 3 (без СБП)
 *
 * Rules (acceptance §2.2, PM decision 2026-05-21 §7):
 *   locale='ru' + telegramLang='ru'  → [Картой, Крипта, СБП, Stars]
 *   иначе                             → [Картой, Крипта,       Stars]
 *
 * СБП is FULLY REMOVED from DOM (not visibility:hidden) when unavailable.
 *
 * Props:
 *   value        ('card'|'crypto'|'sbp'|'stars') — controlled value
 *   onChange     (fn(method: string)) — called on card tap
 *   locale       ('ru'|'en'|string)
 *   telegramLang ('ru'|'en'|string|null) — Telegram initData language_code
 *
 * Icons: /public/icons/payment/{card,crypto,sbp,star}.svg — multi-color brand
 * assets rendered via <img> (not CSS mask).
 */
import { useTranslation } from 'react-i18next';
import { getAvailableMethods } from '../../utils/payment-availability';

/** Icon file path per method (relative to public root) */
const METHOD_ICON = {
  card: '/icons/payment/card.svg',
  crypto: '/icons/payment/crypto.svg',
  sbp: '/icons/payment/sbp.svg',
  stars: '/icons/payment/star.svg',
};

/**
 * Brand-list subtitle per method (Figma: SemiBold 10, #fff).
 * card label сам по себе многострочный → subtitle для card отсутствует визуально на frame
 * Stars (40000032:6651/6653 в Figma) — без subtitle (label «Telegram Stars» в 2 строки заполняет место).
 */
const METHOD_SUBTITLE_KEY = {
  card: 'cardSubtitle',
  crypto: 'cryptoSubtitle',
  sbp: 'sbpSubtitle',
};

export default function PaymentMethodPicker({
  value,
  onChange,
  locale = '',
  telegramLang = '',
  className = '',
}) {
  const { t } = useTranslation();
  const availableMethods = getAvailableMethods({ locale, telegramLang });

  return (
    <div
      className={['flex items-stretch gap-2', className].filter(Boolean).join(' ')}
      role="group"
      aria-label={t('plans.paymentMethods.aria')}
    >
      {availableMethods.map((method) => {
        const isSelected = value === method;
        const subtitleKey = METHOD_SUBTITLE_KEY[method] ?? null;
        const subtitle = subtitleKey ? t(`plans.paymentMethods.${subtitleKey}`) : null;

        return (
          <button
            key={method}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(method)}
            className={[
              // Figma 319:4636 method card: 87×98, VERTICAL layoutMode,
              // primaryAxisAlignItems=MIN (top), counterAxisAlignItems=MIN (left),
              // itemSpacing=8, padding 8/8/8/8, cornerRadius 16.
              // Fill bg-glass-16 (16% white) ВСЕГДА. Selected меняет ТОЛЬКО
              // border на 1px gradient orange (per Figma 319:4636.strokes
              // GRADIENT_LINEAR 135° #FFC24C → #FF9012, strokeWeight 1, INSIDE).
              'flex flex-col items-start gap-2',
              'flex-1 min-w-0 h-[98px] p-2',
              'rounded-home-16 bg-glass-16',
              'border',
              isSelected ? 'border-accent-orange-start' : 'border-transparent',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {/* Icon 32×32 */}
            <img
              src={METHOD_ICON[method]}
              alt=""
              aria-hidden="true"
              width={32}
              height={32}
              className="shrink-0"
            />

            {/* Method label — Figma SemiBold 12 text/primary, left-aligned */}
            <span
              className={[
                'font-sans font-semibold text-[12px] leading-[14.088px] text-left',
                'text-text-primary',
              ].join(' ')}
            >
              {t(`plans.paymentMethods.${method}`)}
            </span>

            {/* Subtitle — Figma SemiBold 10 text/primary opacity 0.48, left-aligned */}
            {subtitle && (
              <span className="font-sans font-semibold text-[10px] leading-[11.74px] text-text-primary text-left opacity-[0.48]">
                {subtitle}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
